/**
 * GeminiLimitsCard — admin-facing view of daily usage limits.
 *
 * Shows:
 *   • Per-user daily limit + enable toggle (LIMIT ONLY — personal usage is
 *     displayed inside the user's own OCR modal, never here as a bogus 0).
 *   • Global daily limit + used_today + remaining + enable toggle.
 *   • Reset time in Asia/Riyadh.
 *   • Edit / toggle actions — super_admin only. Non-super_admin gets a
 *     read-only view. The backend enforces 403 for non-super_admin PUTs.
 *
 * Reuses `/api/admin/gemini/usage-snapshot` (read) and
 * `/api/admin/gemini/config` (write). No new limits system.
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

function fmtRiyadh(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("ar-SA", {
      timeZone: "Asia/Riyadh",
      hour: "2-digit", minute: "2-digit",
      day: "2-digit", month: "2-digit", year: "numeric",
    });
  } catch { return iso; }
}

export default function GeminiLimitsCard({ canEdit }) {
  const [snap, setSnap] = useState(null);
  const [cfg, setCfg]   = useState(null);
  const [err, setErr]   = useState("");
  const [busy, setBusy] = useState(false);
  // Fire the "بلغت ٨٠٪" toast at most once per page open so the admin
  // isn't spammed each time the card polls or a save() reloads it.
  const alertToastFiredRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const [s, c] = await Promise.all([
        fetch(`${API}/api/admin/gemini/usage-snapshot`, { credentials: "include" }).then(r => r.json()),
        fetch(`${API}/api/admin/gemini/config`, { credentials: "include" }).then(r => r.json()),
      ]);
      setSnap(s); setCfg(c); setErr("");
      // Early-warning toast — only when alert is active AND we haven't
      // already shown it this session. Not shown when the global limit
      // is already reached (that's a different, harder banner below).
      if (s?.alert_active && !s?.blocked && !alertToastFiredRef.current) {
        alertToastFiredRef.current = true;
        const pct = Math.round((s.alert_pct_used || 0) * 100);
        toast.warning(
          `تنبيه: بلغ استخدام Gemini اليومي ${pct}٪ من الحد (العتبة ${s.alert_threshold_pct}٪)`,
          { duration: 8000 }
        );
      }
    } catch (e) { setErr("تعذّر تحميل الحدود"); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    if (!canEdit) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/admin/gemini/config`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      if (!r.ok) throw new Error(await r.text());
      await load();
    } catch (e) { setErr("تعذّر الحفظ"); }
    finally { setBusy(false); }
  };

  if (!snap || !cfg) return <div data-testid="gemini-limits-loading" className="text-sm text-slate-500">جارٍ التحميل...</div>;

  const perUserOn = !!snap?.enabled?.per_user;
  const globalOn  = !!snap?.enabled?.global;

  return (
    <div data-testid="gemini-limits-card" className="border-2 border-slate-200 rounded-2xl p-5 bg-white">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-slate-900">حدود Gemini اليومية</h3>
        <span data-testid="gemini-limits-reset" className="text-xs text-slate-500">
          إعادة التعيين (توقيت الرياض): <b>{fmtRiyadh(snap.reset_at_riyadh)}</b>
        </span>
      </div>
      {err && <div className="text-sm text-red-600 mb-3">{err}</div>}

      {/* 80%-of-limit early-warning banner — only when the global cap is
          enabled and we've crossed alert_threshold_pct. Suppressed once
          the cap is fully reached (a separate, stronger message renders
          inside the global panel below). */}
      {snap.alert_active && !snap.blocked && (
        <div data-testid="gemini-limits-alert"
             className="flex items-start gap-3 border-2 border-amber-300 bg-amber-50 rounded-xl p-3 mb-3">
          <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <b>تنبيه:</b> بلغ استخدام Gemini اليومي{" "}
            <b data-testid="alert-pct">{Math.round((snap.alert_pct_used || 0) * 100)}٪</b>{" "}
            من الحد اليومي (العتبة{" "}
            <b>{snap.alert_threshold_pct}٪</b>). عند بلوغ 100٪ سيتوقف البحث بالصور تلقائيًا.
          </div>
        </div>
      )}

      {/* Per-user LIMIT ONLY — no bogus 'used' number here */}
      <div data-testid="gemini-limits-per-user" className="border rounded-xl p-4 mb-3 bg-slate-50">
        <div className="flex items-center justify-between mb-2">
          <div className="font-bold text-slate-900">الحد اليومي لكل زائر</div>
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" data-testid="toggle-per-user" checked={perUserOn}
                   disabled={!canEdit || busy}
                   onChange={(e) => save({ limit_daily_per_user_enabled: e.target.checked })} />
            <span>{perUserOn ? "مفعَّل" : "مُعطَّل"}</span>
          </label>
        </div>
        {perUserOn ? (
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-600">الحد:</span>
            <input type="number" min={1} data-testid="input-per-user"
                   defaultValue={snap.limit_daily_per_user ?? cfg.limit_daily_per_user}
                   disabled={!canEdit || busy}
                   onBlur={(e) => save({ limit_daily_per_user: Math.max(1, Number(e.target.value) || 1) })}
                   className="w-24 border rounded px-2 py-1 text-sm" />
            <span className="text-xs text-slate-500">(الاستهلاك الشخصي يظهر في نافذة البحث بالصورة لكل مستخدم)</span>
          </div>
        ) : (
          <div data-testid="per-user-open" className="text-sm text-emerald-700">الاستخدام مفتوح</div>
        )}
      </div>

      {/* Global — full usage panel */}
      <div data-testid="gemini-limits-global" className="border rounded-xl p-4 bg-slate-50">
        <div className="flex items-center justify-between mb-2">
          <div className="font-bold text-slate-900">الحد اليومي لجميع الزوار</div>
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" data-testid="toggle-global" checked={globalOn}
                   disabled={!canEdit || busy}
                   onChange={(e) => save({ limit_daily_global_enabled: e.target.checked })} />
            <span>{globalOn ? "مفعَّل" : "مُعطَّل"}</span>
          </label>
        </div>
        {globalOn ? (
          <>
            <div className="grid grid-cols-3 gap-3 mb-2">
              <div><div className="text-xs text-slate-500">المستخدم اليوم</div>
                   <div data-testid="global-used" className="text-lg font-bold">{snap.used_today_global ?? 0}</div></div>
              <div><div className="text-xs text-slate-500">المتبقي</div>
                   <div data-testid="global-remaining" className="text-lg font-bold">{snap.remaining_global ?? 0}</div></div>
              <div><div className="text-xs text-slate-500">الحد</div>
                   <div data-testid="global-limit" className="text-lg font-bold">{snap.limit_daily_global ?? 0}</div></div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-slate-600">تعديل الحد:</span>
              <input type="number" min={1} data-testid="input-global"
                     defaultValue={snap.limit_daily_global ?? cfg.limit_daily_global}
                     disabled={!canEdit || busy}
                     onBlur={(e) => save({ limit_daily_global: Math.max(1, Number(e.target.value) || 1) })}
                     className="w-28 border rounded px-2 py-1 text-sm" />
              {snap.blocked && snap.blocked_reason === "global" && (
                <span className="text-xs text-red-700 font-bold">تم بلوغ الحد اليوم — البحث بالصور موقوف حتى إعادة التعيين</span>
              )}
            </div>
          </>
        ) : (
          <div data-testid="global-open" className="text-sm text-emerald-700">الاستخدام مفتوح</div>
        )}
      </div>

      {/* Proof-of-call panel (Feb 2026). Every number here reflects ONLY
          rows written after a real Gemini API call — cache / barcode /
          local search / DeepSeek / polling / limit-blocks never appear.
          Range defaults to today in Asia/Riyadh. */}
      <div data-testid="gemini-real-usage-card"
           className="border-2 border-slate-200 rounded-xl p-4 mt-4 bg-slate-50">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-bold text-slate-900">استخدام Gemini — اليوم (Asia/Riyadh)</h4>
          <span data-testid="real-usage-range" className="text-xs text-slate-500">
            حتى إعادة الضبط:{" "}
            <b>{fmtRiyadh(snap?.today_proof?.date_range?.end_riyadh || snap?.reset_at_riyadh)}</b>
          </span>
        </div>
        {(() => {
          const p = snap?.today_proof || {};
          const noReqs = !p.requests_today;
          return (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="bg-white border rounded p-3">
                  <div className="text-xs text-slate-500">عدد الطلبات</div>
                  <div data-testid="real-requests" className="text-2xl font-bold" dir="ltr">
                    {p.requests_today ?? 0}
                  </div>
                </div>
                <div className="bg-white border rounded p-3">
                  <div className="text-xs text-slate-500">‏API attempts</div>
                  <div data-testid="real-attempts" className="text-2xl font-bold" dir="ltr">
                    {p.api_attempts_today ?? 0}
                  </div>
                </div>
                <div className="bg-white border rounded p-3">
                  <div className="text-xs text-slate-500">‏Input Tokens</div>
                  <div data-testid="real-input-tokens" className="text-xl font-mono" dir="ltr">
                    {p.input_tokens ?? 0}
                  </div>
                </div>
                <div className="bg-white border rounded p-3">
                  <div className="text-xs text-slate-500">‏Output Tokens</div>
                  <div data-testid="real-output-tokens" className="text-xl font-mono" dir="ltr">
                    {p.output_tokens ?? 0}
                  </div>
                </div>
                <div className="bg-white border rounded p-3">
                  <div className="text-xs text-slate-500">‏Total Tokens · Cost</div>
                  <div className="text-sm font-mono" dir="ltr">
                    <span data-testid="real-total-tokens">{p.total_tokens ?? 0}</span>
                    {" · $"}
                    <span data-testid="real-cost-usd">{Number(p.cost_usd_today ?? 0).toFixed(4)}</span>
                  </div>
                </div>
              </div>
              {noReqs && (
                <div data-testid="real-empty" className="mt-3 text-sm text-slate-700 font-bold">
                  0 طلب — لا توجد Tokens مسجلة اليوم.
                </div>
              )}
              {!noReqs && !p.tokens_recorded && (
                <div data-testid="real-tokens-missing" className="mt-2 text-xs text-amber-700">
                  الاستدعاءات تمت لكن مزود Gemini لم يُعِد usage — لا توجد Tokens مسجلة.
                </div>
              )}
              {(p.legacy_rows_today ?? 0) > 0 && (
                <div data-testid="real-legacy-badge"
                     className="mt-2 text-xs text-slate-500 border-t pt-2">
                  سجلات قديمة غير موثّقة (‏قبل proof-of-call) اليوم:{" "}
                  <b data-testid="legacy-count">{p.legacy_rows_today}</b>{" "}
                  — مستبعدة من الأرقام أعلاه (‏لا نستخدم قيمها ولا نعدّلها).
                </div>
              )}
              <div className="mt-2 text-[11px] text-slate-500">
                يُحتسب الطلب فقط عند استدعاء Gemini فعلياً · Cache/Barcode/محلي/DeepSeek/Polling لا تُحتسب
                · التوكِنات من استجابة Gemini حصراً (‏لا تقديرات، لا max_tokens).
              </div>
            </>
          );
        })()}
      </div>

      {!canEdit && (
        <div className="mt-3 text-xs text-slate-500">
          للعرض فقط — التعديل مقتصر على <b>super_admin</b>.
        </div>
      )}
    </div>
  );
}
